import { useQuery } from "@tanstack/react-query";

import { queryKeys } from "@/api/query-keys";
import { sensorsApi } from "@/api/sensors";

interface UseSensorSummaryOptions {
	/** How often to re-fetch in milliseconds. Defaults to 60 000 (1 min). */
	refetchInterval?: number;
}

/**
 * Hook to fetch sensor status summary for dashboard display
 *
 * Returns:
 * - online: Count of sensors in active state
 * - offline: Count of sensors in inactive state
 * - warning: Count of sensors in error state
 * - total: Total count of all sensors
 *
 * All callers share the same React Query cache entry; the shortest active
 * interval wins while multiple components are mounted simultaneously.
 */
export const useSensorSummary = ({
	refetchInterval = 60000, // Default to 60 seconds
}: UseSensorSummaryOptions = {}) => {
	const {
		data: sensorSummaryData,
		error: sensorSummaryError,
		isError: isSensorSummaryError,
		isLoading: isLoadingSensorSummary,
		isSuccess: isSensorSummarySuccess,
		refetch: refetchSensorSummary,
	} = useQuery({
		queryFn: () => sensorsApi.getSummary(),
		queryKey: queryKeys.sensors.summary(),
		refetchInterval,
	});
	return {
		isLoadingSensorSummary,
		isSensorSummaryError,
		isSensorSummarySuccess,
		refetchSensorSummary,
		sensorSummaryData,
		sensorSummaryError,
	};
};
